import { useEffect, useMemo, useState } from "react";
import { Controller, useFormContext, useWatch } from "react-hook-form";
import {
  AlertTriangle,
  ArrowRight,
  Info,
  RotateCcw,
  Search,
  UserCheck,
  UserPlus,
} from "lucide-react";
import type { UpcomingBooking } from "@mtform/shared";
import { useAuth } from "@/auth/AuthContext";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { Field } from "@/components/ui/field";
import { Input } from "@/components/ui/input";
import { Spinner } from "@/components/ui/spinner";
import { DateField } from "@/components/form/DateField";
import {
  formatIsoDateForDisplay,
  normalizeDateForPayload,
  todayIso,
} from "@/lib/format";
import { prettyStatus, statusBadgeClasses } from "@/lib/trip-status";
import { cn } from "@/lib/utils";
import { Pencil } from "lucide-react";
import { titleCaseName } from "@/lib/format";
import { EditPatientNameDialog } from "@/components/patient/EditPatientNameDialog";
import { WizardCard } from "../WizardCard";
import { usePatientLookupState } from "../PatientLookupContext";
import type { TripFormValues } from "../schema";
import type { WizardStepStatus } from "../WizardCard";

interface PatientLookupStepProps {
  index: number;
  status: WizardStepStatus;
  /** Advance to the next step (details). */
  onContinue: () => void;
  /** Fired by the inline "No, wrong patient" button on the verification
   *  card. Already an explicit user choice — the parent should reset
   *  immediately without an extra confirmation prompt. */
  onWrongPatient: () => void;
  /** Fired by the Edit button on the collapsed identifier card after the
   *  user has passed through. The parent should prompt before wiping the
   *  form — at this point later steps may already be filled in and a
   *  misclick shouldn't blow them away. */
  onChangeIdentifier: () => void;
}

// =============================================================================
// PatientLookupStep
//
// Single card that handles both the MRN/DOB inputs and — when the lookup
// matches a returning patient — the inline "is this the right patient?"
// verification. Folding the two into one card avoids a redundant
// "Continue → confirm same name → Continue" double-click without losing
// the deliberate read-and-confirm moment dispatch needs to catch typos.
//
// Three branches in the active state:
//   1. Found        → big card with the patient's name + "Yes / No" CTAs.
//   2. Not found    → "New patient" banner + Continue (advances to details).
//   3. Loading/idle → Continue button, disabled while lookup is in flight.
// =============================================================================

export function PatientLookupStep({
  index,
  status,
  onContinue,
  onWrongPatient,
  onChangeIdentifier,
}: PatientLookupStepProps) {
  const {
    control,
    register,
    formState: { errors },
    trigger,
  } = useFormContext<TripFormValues>();

  const lookup = usePatientLookupState();
  const mrn = useWatch({ control, name: "mrn" }) ?? "";
  const dob = useWatch({ control, name: "dob" }) ?? "";

  const trimmedMrn = String(mrn).trim();
  const isoDob = normalizeDateForPayload(dob);
  const lookupBusy = lookup.status === "loading";
  const continueDisabled = !trimmedMrn || !isoDob || lookupBusy;

  async function handleContinue() {
    const ok = await trigger(["mrn", "dob"]);
    if (!ok) return;
    onContinue();
  }

  if (status === "complete") {
    const summary = (() => {
      const dobDisplay = isoDob ? formatIsoDateForDisplay(isoDob) : dob;
      const parts = [
        trimmedMrn ? `MRN ${trimmedMrn}` : null,
        dobDisplay ? `DOB ${dobDisplay}` : null,
      ];
      return parts.filter(Boolean).join(" · ");
    })();
    return (
      <WizardCard
        index={index}
        title="Patient identifier"
        status="complete"
        summary={summary || "—"}
        onEdit={onChangeIdentifier}
      />
    );
  }

  const isFound = lookup.status === "found" && Boolean(lookup.patient);

  return (
    <WizardCard
      index={index}
      title="Look up the patient"
      description="Enter MRN and date of birth so we can pull anything we already have on file."
      status={status}
    >
      <div className="grid grid-cols-1 gap-3 md:grid-cols-2">
        <Field label="MRN #" required htmlFor="mrn" error={errors.mrn?.message}>
          <Input
            id="mrn"
            autoComplete="off"
            autoFocus
            maxLength={50}
            clearable
            {...register("mrn")}
          />
        </Field>
        <Field
          label="Date of birth"
          required
          htmlFor="dob"
          error={errors.dob?.message}
        >
          <Controller
            control={control}
            name="dob"
            render={({ field, fieldState }) => (
              <DateField
                id="dob"
                value={field.value ?? ""}
                onChange={field.onChange}
                onBlur={field.onBlur}
                invalid={Boolean(fieldState.error)}
                pastOnly
              />
            )}
          />
        </Field>
      </div>

      {isFound ? (
        <FoundPatientCard onYes={onContinue} onNo={onWrongPatient} />
      ) : (
        <>
          <LookupStatusBanner />

          <div className="flex flex-wrap items-center justify-between gap-2 pt-1">
            <p className="text-xs text-muted-foreground">
              New patient? Continue and we'll save their info after the first booking.
            </p>
            <Button
              type="button"
              onClick={handleContinue}
              disabled={continueDisabled}
              className="min-w-[10rem]"
            >
              {lookupBusy ? (
                <>
                  <Spinner /> Looking up…
                </>
              ) : (
                <>
                  Continue
                  <ArrowRight className="h-4 w-4" />
                </>
              )}
            </Button>
          </div>
        </>
      )}
    </WizardCard>
  );
}

// =============================================================================
// FoundPatientCard
//
// Renders inside the lookup card whenever the BFF matches a returning
// patient. Shows the name (the whole point of this prompt — staff have
// to read it) and asks Yes / No before letting them advance.
// =============================================================================

function FoundPatientCard({
  onYes,
  onNo,
}: {
  onYes: () => void;
  onNo: () => void;
}) {
  const lookup = usePatientLookupState();
  const auth = useAuth();
  const formCtx = useFormContext<TripFormValues>();
  const [acknowledgedDuplicate, setAcknowledgedDuplicate] = useState(false);
  const [editOpen, setEditOpen] = useState(false);
  // Local override applied immediately on successful rename so the
  // displayed name updates without forcing a re-lookup (the lookup
  // hook caches its result in local state, not TanStack Query, so an
  // imperative invalidation would mean re-typing MRN/DOB to refetch).
  // Cleared whenever the underlying lookup changes (different MRN).
  const [nameOverride, setNameOverride] = useState<{
    first: string;
    last: string;
  } | null>(null);
  const lookupKey =
    lookup.status === "found" && lookup.patient
      ? `${lookup.patient.firstName}|${lookup.patient.lastName}`
      : "";
  useEffect(() => {
    setNameOverride(null);
  }, [lookupKey]);

  const currentUserId =
    auth.state.status === "authenticated" ? auth.state.user.id : null;

  const upcomingBookings =
    lookup.status === "found" && lookup.patient
      ? lookup.patient.upcomingBookings
      : [];

  const hasHighSignal = useMemo(
    () => upcomingBookings.some(isHighSignal),
    [upcomingBookings],
  );

  if (lookup.status !== "found" || !lookup.patient) return null;

  const effectiveFirst =
    nameOverride?.first ?? lookup.patient.firstName;
  const effectiveLast = nameOverride?.last ?? lookup.patient.lastName;
  const fullName = titleCaseName(
    `${effectiveFirst} ${effectiveLast}`.trim(),
  );
  const dobDisplay = formatIsoDateForDisplay(lookup.patient.dob);
  const visitLabel =
    lookup.patient.bookingCount && lookup.patient.bookingCount > 0
      ? lookup.patient.bookingCount === 1
        ? "1 prior booking"
        : `${lookup.patient.bookingCount} prior bookings`
      : "Returning patient";

  // High-signal duplicates require an explicit acknowledgment before
  // the "Yes, that's right" button enables — that's the whole point
  // of the warning. Low-signal informational notes don't gate.
  const yesDisabled = hasHighSignal && !acknowledgedDuplicate;

  return (
    <div
      role="status"
      aria-live="polite"
      className="rounded-2xl border border-success/35 bg-success/[0.06] px-4 py-4"
    >
      <div className="flex items-start gap-3">
        <span className="flex h-10 w-10 flex-shrink-0 items-center justify-center rounded-full bg-success/15 text-success">
          <UserCheck className="h-5 w-5" />
        </span>
        <div className="flex min-w-0 flex-col gap-0.5">
          <p className="text-[0.65rem] font-bold uppercase tracking-widest text-success">
            {visitLabel} · is this the right patient?
          </p>
          <p className="text-xl font-bold leading-tight text-foreground">
            <span>{fullName || "(name on file)"}</span>
            <button
              type="button"
              onClick={() => setEditOpen(true)}
              className="ml-1.5 inline-flex h-7 w-7 items-center justify-center rounded-full text-muted-foreground transition hover:bg-foreground/[0.06] hover:text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring align-middle"
              title="Fix the spelling or casing of this patient's name"
              aria-label={`Edit name for MRN ${
                String(formCtx.getValues("mrn") ?? "").trim() || "this patient"
              }`}
            >
              <Pencil className="h-3.5 w-3.5" />
            </button>
          </p>
          <p className="text-sm text-foreground/80">DOB {dobDisplay}</p>
        </div>
      </div>

      {/* Inline rename — see EditPatientNameDialog for the full backend
          flow (updates bookings + patients + writes audit row). We
          mirror the result into the trip-form's firstName/lastName so
          the booking the user is about to submit picks up the
          corrected spelling without waiting for a relookup. */}
      <EditPatientNameDialog
        mrn={String(formCtx.getValues("mrn") ?? "").trim()}
        initialFirst={effectiveFirst}
        initialLast={effectiveLast}
        open={editOpen}
        onOpenChange={setEditOpen}
        onRenamed={(first, last) => {
          setNameOverride({ first, last });
          formCtx.setValue("firstName", first, { shouldDirty: true });
          formCtx.setValue("lastName", last, { shouldDirty: true });
        }}
      />

      {upcomingBookings.length > 0 ? (
        <DuplicateTripWarning
          bookings={upcomingBookings}
          currentUserId={currentUserId}
          acknowledged={acknowledgedDuplicate}
          onAcknowledge={setAcknowledgedDuplicate}
        />
      ) : null}

      <div className="mt-4 flex flex-wrap items-center justify-between gap-2">
        <Button
          type="button"
          variant="secondary"
          onClick={onNo}
          className="min-w-[11rem]"
        >
          <RotateCcw className="h-4 w-4" /> No, wrong patient
        </Button>
        <Button
          type="button"
          onClick={onYes}
          disabled={yesDisabled}
          className="min-w-[11rem]"
        >
          Yes, that's right
          <ArrowRight className="h-4 w-4" />
        </Button>
      </div>
    </div>
  );
}

// =============================================================================
// DuplicateTripWarning
//
// Surfaces existing-trip context on the patient-confirmation card so
// the requester catches a potential duplicate before booking another.
// Two tiers (see `isHighSignal` for the rule):
//
//   - High-signal (amber): same-day trip on the books, or any trip
//     booked within the last 6h. Acknowledgment is required before
//     "Yes, that's right" enables — the duplicate-prevention happens
//     here, not later.
//   - Low-signal (slate): future-dated trips, not recently created.
//     Informational only — frequent flyers (3×/week dialysis etc.)
//     just see a calm "patient has N upcoming trips" panel.
//
// Same-user vs other-user wording shifts subtly: when the trip was
// booked by the current viewer the lead-in becomes "you booked this"
// rather than the booker's name.
// =============================================================================

function DuplicateTripWarning({
  bookings,
  currentUserId,
  acknowledged,
  onAcknowledge,
}: {
  bookings: UpcomingBooking[];
  currentUserId: string | null;
  acknowledged: boolean;
  onAcknowledge: (checked: boolean) => void;
}) {
  const highSignal = bookings.some(isHighSignal);
  const tone = highSignal ? "amber" : "slate";
  const title = highSignal
    ? bookings.length === 1
      ? "This patient already has a trip booked"
      : `This patient already has ${bookings.length} trips booked`
    : bookings.length === 1
      ? "Patient has 1 upcoming trip on file"
      : `Patient has ${bookings.length} upcoming trips on file`;

  return (
    <div
      role={highSignal ? "alert" : "status"}
      aria-live="polite"
      className={cn(
        "mt-4 rounded-2xl border px-3 py-3",
        tone === "amber"
          ? "border-amber-400/45 bg-amber-400/[0.08]"
          : "border-border bg-foreground/[0.03]",
      )}
    >
      <div className="flex items-start gap-2">
        {highSignal ? (
          <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700 dark:text-amber-300" />
        ) : (
          <Info className="mt-0.5 h-5 w-5 flex-shrink-0 text-muted-foreground" />
        )}
        <div className="flex min-w-0 flex-col gap-0.5">
          <p
            className={cn(
              "text-sm font-bold",
              tone === "amber" ? "text-amber-700 dark:text-amber-200" : "text-foreground",
            )}
          >
            {title}
          </p>
          {highSignal ? (
            <p className="text-xs text-foreground/85">
              Worth a quick check with the requester first — if one of
              these is the trip they meant, no new booking is needed.
            </p>
          ) : null}
        </div>
      </div>

      <ul className="mt-2 space-y-1.5 pl-7">
        {orderedForDisplay(bookings).slice(0, MAX_LISTED_BOOKINGS).map((b) => (
          <li
            key={b.friendlyId}
            className="flex flex-wrap items-baseline gap-x-2 gap-y-0.5 text-xs"
          >
            <span className="font-mono font-semibold text-foreground/90">
              {b.friendlyId}
            </span>
            {b.status ? (
              // Live triplink status — tells the dispatcher whether the
              // potential-duplicate trip is still pending (real risk) vs
              // already en route / finished (lower risk that this is a
              // confused double-booking).
              <span
                className={cn(
                  "rounded-full border px-1.5 py-px text-[0.6rem] font-bold uppercase tracking-wider",
                  statusBadgeClasses(b.status),
                )}
              >
                {prettyStatus(b.status)}
              </span>
            ) : null}
            <span className="text-foreground/75">{formatTripWhen(b)}</span>
            <span className="text-muted-foreground">
              ({formatBooker(b, currentUserId)})
            </span>
          </li>
        ))}
      </ul>
      {bookings.length > MAX_LISTED_BOOKINGS ? (
        <p className="mt-1.5 pl-7 text-xs text-muted-foreground">
          + {bookings.length - MAX_LISTED_BOOKINGS} more upcoming trip
          {bookings.length - MAX_LISTED_BOOKINGS === 1 ? "" : "s"} on file.
        </p>
      ) : null}

      {highSignal ? (
        // Inline checkbox reads more naturally than a dark "Acknowledged"
        // pill that visually fights with the amber warning surface. The
        // label is the affordance, the box is the gate — and unticking
        // is allowed in case the dispatcher wants a second look at the
        // list before continuing.
        <label className="mt-3 ml-7 flex cursor-pointer select-none items-center gap-2">
          <Checkbox
            checked={acknowledged}
            onCheckedChange={(c) => onAcknowledge(c === true)}
            className={cn(
              acknowledged &&
                "border-amber-300 bg-amber-300 data-[state=checked]:border-amber-300 data-[state=checked]:bg-amber-300",
            )}
          />
          <span
            className={cn(
              "text-xs",
              acknowledged
                ? "font-semibold text-amber-700 dark:text-amber-200/90"
                : "text-foreground/85",
            )}
          >
            {acknowledged
              ? "Reviewed — go ahead with the new booking"
              : "I've reviewed these — this is a different trip"}
          </span>
        </label>
      ) : null}
    </div>
  );
}

/** The lookup returns up to UPCOMING_LIMIT (50) trips so the same-date
 *  collision check downstream sees a frequent flyer's whole schedule. The
 *  panel is a glance, not a manifest — list a handful and count the rest, so a
 *  3x/week dialysis patient doesn't bury the step in rows. */
const MAX_LISTED_BOOKINGS = 5;

/** High-signal rows first, then the rest in their original order.
 *
 *  The list arrives sorted by transport date, but a row can be high-signal
 *  because it was booked in the last 6h REGARDLESS of its date — a trip booked
 *  ten minutes ago for three weeks out sorts far down. Without this it could
 *  fall past the display cap, leaving the panel saying "if one of these is the
 *  trip they meant" while the trip that raised the alarm is hidden behind
 *  "+N more". The escalation itself always considered the whole list; this is
 *  about showing the booker what they are being asked to confirm. */
function orderedForDisplay(bookings: UpcomingBooking[]): UpcomingBooking[] {
  return [
    ...bookings.filter(isHighSignal),
    ...bookings.filter((b) => !isHighSignal(b)),
  ];
}

const SIX_HOURS_MS = 6 * 60 * 60 * 1000;

function isHighSignal(b: UpcomingBooking): boolean {
  // Same-day trip is always the high-signal case (most likely
  // duplicate scenario). Also escalate anything booked within the
  // last 6h regardless of transport date — catches the simultaneous-
  // booking case where two dispatchers race to book the same trip.
  if (b.transportDate === todayIso()) return true;
  const createdMs = Date.parse(b.createdAt);
  if (!Number.isFinite(createdMs)) return false;
  return Date.now() - createdMs < SIX_HOURS_MS;
}

function formatTripWhen(b: UpcomingBooking): string {
  const date = b.transportDate
    ? formatIsoDateForDisplay(b.transportDate)
    : "";
  const isToday = b.transportDate === todayIso();
  const datePart = isToday ? "Today" : date || "—";
  const time =
    b.pickupTimeEstimated === "WILL_CALL"
      ? "Will Call"
      : (b.appointmentTime || b.pickupTimeEstimated || "").trim();
  return time ? `${datePart} at ${time}` : datePart;
}

function formatBooker(
  b: UpcomingBooking,
  currentUserId: string | null,
): string {
  const ago = formatRelativeAgo(Date.now() - Date.parse(b.createdAt));
  if (b.createdByUserId && b.createdByUserId === currentUserId) {
    return `you booked this ${ago}`;
  }
  const who = b.createdByName || "someone";
  return `${who}, ${ago}`;
}

function formatRelativeAgo(ms: number): string {
  if (!Number.isFinite(ms) || ms < 0) return "just now";
  if (ms < 60_000) return "just now";
  if (ms < 60 * 60_000) return `${Math.floor(ms / 60_000)} min ago`;
  if (ms < 24 * 60 * 60_000) return `${Math.floor(ms / (60 * 60_000))}h ago`;
  if (ms < 7 * 24 * 60 * 60_000)
    return `${Math.floor(ms / (24 * 60 * 60_000))}d ago`;
  return "earlier";
}

function LookupStatusBanner() {
  const lookup = usePatientLookupState();
  if (lookup.status === "idle") return null;

  if (lookup.status === "error") {
    // A failed lookup used to render nothing, which silently dropped
    // the prefill AND the duplicate-trip check — the dispatcher had
    // no idea the safety net was gone. Surface it as a non-blocking
    // warning: they can still continue, but they know what's missing.
    return (
      <div
        role="alert"
        className="flex items-start gap-3 rounded-2xl border border-amber-400/45 bg-amber-400/[0.08] px-4 py-3"
      >
        <AlertTriangle className="mt-0.5 h-5 w-5 flex-shrink-0 text-amber-700 dark:text-amber-300" />
        <div className="flex min-w-0 flex-col gap-1">
          <p className="text-sm font-bold text-amber-700 dark:text-amber-200">
            We couldn't check this patient's record
          </p>
          <p className="text-sm text-foreground/85">
            You can continue with the booking, but saved info won't be
            filled in and the duplicate-trip check is unavailable.
          </p>
          <Button
            type="button"
            variant="secondary"
            size="sm"
            onClick={() => lookup.retry()}
            className="mt-1 self-start"
          >
            <RotateCcw className="h-3.5 w-3.5" /> Retry lookup
          </Button>
        </div>
      </div>
    );
  }

  if (lookup.status === "loading") {
    return (
      <Hint>
        <Spinner /> Looking up patient…
      </Hint>
    );
  }

  if (lookup.status === "not-found") {
    return (
      <div
        role="status"
        aria-live="polite"
        className="flex items-start gap-3 rounded-2xl border border-primary/45 bg-primary/10 px-4 py-3"
      >
        <UserPlus className="mt-0.5 h-5 w-5 flex-shrink-0 text-primary" />
        <div className="flex flex-col gap-0.5">
          <p className="text-sm font-bold text-primary">New patient</p>
          <p className="text-sm text-foreground">
            We don't have a record for this MRN + DOB yet. Continue to fill in
            their info — we'll save it for next time.
          </p>
        </div>
      </div>
    );
  }

  return null;
}

function Hint({ children }: { children: React.ReactNode }) {
  return (
    <div className="flex items-center gap-2 rounded-xl border border-border bg-foreground/[0.03] px-3 py-2 text-xs text-muted-foreground">
      <Search className="h-3.5 w-3.5 text-muted-foreground" /> {children}
    </div>
  );
}
